import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, input, InputSignal, output, OutputEmitterRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { AgGridAngular } from 'ag-grid-angular';
import { tap } from 'rxjs';
import type {
    BodyScrollEvent,
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    HeaderValueGetterParams,
    ValueFormatterParams
} from 'ag-grid-community';
import { shsThemeDark } from '../../../ag-grid';
import { SelectedLanguageLocaleService } from '../../../util/selected-language-locale.service';
import { formatLocalizedDate } from '../../../pipes/localized-date.pipe';
import { ITableActivity } from './models/table-acitivty.model';
import { ActivityStatusCellComponent } from './cells/activity-status-cell.component';
import { SCROLL_THRESHOLD } from './activity-stream.config';

@Component({
    selector: 'app-activity-stream',
    templateUrl: './activity-stream.component.html',
    imports: [AgGridAngular],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    host: { class: 'flex flex-col flex-1 min-h-0 overflow-hidden' }
})
export class ActivityStreamComponent {
    public items: InputSignal<ITableActivity[]> = input<ITableActivity[]>([]);
    public loading: InputSignal<boolean> = input<boolean>(true);
    public loadMore: OutputEmitterRef<void> = output<void>();

    private translate = inject(TranslateService);
    protected readonly selectedLanguageLocale = inject(SelectedLanguageLocaleService).getLocale('en-GB');

    private gridApi?: GridApi<ITableActivity>;

    protected noRowsTemplate = `<span>${this.translate.instant('common.request-detail.activity-stream.no-activities')}</span>`;

    constructor() {
        this.translate.onLangChange
            .pipe(
                takeUntilDestroyed(),
                tap(() => {
                    this.noRowsTemplate = `<span>${this.translate.instant('common.request-detail.activity-stream.no-activities')}</span>`;
                    this.gridApi?.refreshHeader();
                })
            )
            .subscribe();
    }

    private headerValue(key: string) {
        return (_: HeaderValueGetterParams) => this.translate.instant(key);
    }

    protected readonly columnDefs: ColDef<ITableActivity>[] = [
        {
            field: 'created',
            headerValueGetter: this.headerValue('common.request-detail.activity-stream.headers.changed'),
            flex: 1,
            minWidth: 180,
            sortable: false,
            valueFormatter: (params: ValueFormatterParams<ITableActivity, Date>) => {
                if (!params.value) {
                    return '';
                }
                const locale = this.selectedLanguageLocale();
                return `${formatLocalizedDate(params.value, 'longDate', locale)} ${formatLocalizedDate(params.value, 'shortTime', locale)}`;
            }
        },
        {
            field: 'action',
            headerValueGetter: this.headerValue('common.request-detail.activity-stream.headers.activity'),
            flex: 4,
            minWidth: 200,
            sortable: false
        },
        {
            field: 'name',
            headerValueGetter: this.headerValue('common.request-detail.activity-stream.headers.by'),
            flex: 2,
            minWidth: 160,
            sortable: false
        },
        {
            colId: 'status',
            headerValueGetter: this.headerValue('common.request-detail.activity-stream.headers.status-change'),
            flex: 1,
            minWidth: 160,
            sortable: false,
            cellRenderer: ActivityStatusCellComponent
        }
    ];

    protected readonly gridOptions: GridOptions<ITableActivity> = {
        theme: shsThemeDark,
        rowHeight: 52,
        headerHeight: 40,
        suppressCellFocus: true,
        defaultColDef: {
            resizable: true,
            cellStyle: { display: 'flex', alignItems: 'center' }
        }
    };

    protected onGridReady(event: GridReadyEvent<ITableActivity>): void {
        this.gridApi = event.api;
    }

    protected onBodyScroll(event: BodyScrollEvent<ITableActivity>): void {
        if (event.direction !== 'vertical' || this.loading()) {
            return;
        }
        const verticalPixelRange = event.api.getVerticalPixelRange();
        const displayedRowCount = event.api.getDisplayedRowCount();
        if (displayedRowCount === 0) {
            return;
        }
        const lastRowNode = event.api.getDisplayedRowAtIndex(displayedRowCount - 1);
        if (!lastRowNode || lastRowNode.rowTop == null) {
            return;
        }
        const lastRowBottom = lastRowNode.rowTop + (lastRowNode.rowHeight ?? 0);
        if (lastRowBottom - verticalPixelRange.bottom <= SCROLL_THRESHOLD) {
            this.loadMore.emit();
        }
    }
}
